import {addCartDetails} from './cart-details'
import {
  addCustomerToConsentLogs,
  createConsentTracker,
  subscribeToOneTrustChanges
} from './consent'
import {createDataCloudDispatcher} from './dispatcher'
import {isDataCloudConfigured, loadDataCloudSdk} from './loader'
import type {
  DataCloudConfiguration,
  DataCloudDispatch,
  GetDataCloudContext,
  WhenBasketSettled
} from './types'

interface StartOptions {
  config: DataCloudConfiguration
  getSitePreference: (id: string) => Promise<unknown>
  isOneTrustEnabled: () => boolean
  getContext?: GetDataCloudContext
  whenBasketSettled?: WhenBasketSettled
  onReady: (dispatch: DataCloudDispatch) => void
  onDisabled?: () => void
}

/**
 * Applies the feature flags, loads the SDK and wires OneTrust consent.
 * Returns a cleanup function.
 */
export const startDataCloud = ({
  config,
  getSitePreference,
  isOneTrustEnabled,
  getContext = () => ({}),
  whenBasketSettled,
  onReady,
  onDisabled = () => undefined
}: StartOptions) => {
  let cancelled = false
  let unsubscribe: () => void = () => undefined
  let removeConsentEnrichment: () => void = () => undefined
  let removeCartDetails: () => void = () => undefined

  const run = async () => {
    if (!isDataCloudConfigured(config)) return onDisabled()
    if (config.sitePreferenceId && (await getSitePreference(config.sitePreferenceId)) !== true) {
      return onDisabled()
    }
    if (cancelled) return

    const consent = createConsentTracker(config.consentGroup || 'C0004', isOneTrustEnabled())
    // Registered before init so the initial consentLog is enriched too
    removeConsentEnrichment = addCustomerToConsentLogs(getContext)
    removeCartDetails = addCartDetails(getContext)
    const sdkReady = loadDataCloudSdk(config, consent.initial(window.OnetrustActiveGroups))
    sdkReady.catch((error) => console.warn('Data Cloud SDK failed to load', error))

    const dispatcher = createDataCloudDispatcher(
      sdkReady,
      {pageVisits: config.pageVisitEvents},
      consent.isOptedIn,
      getContext,
      whenBasketSettled
    )

    unsubscribe = subscribeToOneTrustChanges((groups) => {
      const update = consent.next(groups)
      if (!update) return
      sdkReady
        .then((sdk) => {
          sdk.updateConsents(update)
          if (update.status === 'Opt In') dispatcher.resendPendingIdentity()
        })
        .catch(() => undefined)
    })

    onReady(dispatcher)
  }

  run().catch((error) => {
    console.warn('Data Cloud failed to start', error)
    onDisabled()
  })

  return () => {
    cancelled = true
    unsubscribe()
    removeConsentEnrichment()
    removeCartDetails()
  }
}
